/**
 * Automatische Abmeldung aus Sicherheitsgründen (Finanzdaten der Familien-GbR):
 *  - Inaktivitäts-Logout: 15 Minuten ohne Interaktion → abmelden.
 *  - Harte Sitzungs-Obergrenze: spätestens 60 Minuten nach dem Login → abmelden
 *    (auch bei durchgehender Nutzung; der Token-Auto-Refresh verlängert das NICHT).
 *
 * Umsetzung per Polling (Intervall) statt setTimeout: robust gegen Geräte-Schlaf
 * (Timer werden im Hintergrund gedrosselt/verschoben) — beim Aufwachen bzw. beim
 * Wiedersichtbarwerden des Tabs wird sofort geprüft und ggf. abgemeldet.
 *
 * `bb_session_start` wird beim Login gesetzt (auth.store.applyTokens), NICHT beim
 * stillen Token-Refresh — so bleibt die 60-Min-Grenze fest ab dem Login.
 */
import { onMounted, onUnmounted } from 'vue';

const IDLE_MS = 15 * 60 * 1000;      // 15 Minuten Inaktivität
const ABSOLUTE_MS = 60 * 60 * 1000;  // 60 Minuten harte Obergrenze
const CHECK_MS = 30 * 1000;          // alle 30 Sekunden prüfen
const SESSION_START_KEY = 'bb_session_start';

/** Beim Login aufrufen: Startzeitpunkt der Sitzung merken (für die 60-Min-Grenze). */
export function sessionStartSetzen(): void {
  localStorage.setItem(SESSION_START_KEY, String(Date.now()));
}

/** Beim Abmelden aufrufen. */
export function sessionStartLoeschen(): void {
  localStorage.removeItem(SESSION_START_KEY);
}

const AKTIVITAETS_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'pointerdown'];

/**
 * @param istAngemeldet  Getter, ob gerade ein Nutzer eingeloggt ist.
 * @param abmelden       Callback, der Abmeldung + Weiterleitung erledigt.
 */
export function useAutoLogout(istAngemeldet: () => boolean, abmelden: () => void): void {
  let letzteAktivitaet = Date.now();
  let intervall: number | undefined;

  function aktivitaet(): void {
    letzteAktivitaet = Date.now();
  }

  function pruefen(): void {
    if (!istAngemeldet()) return;
    const start = Number(localStorage.getItem(SESSION_START_KEY) || 0);
    const obergrenzeUeberschritten = start > 0 && Date.now() - start >= ABSOLUTE_MS;
    const zuLangeInaktiv = Date.now() - letzteAktivitaet >= IDLE_MS;
    if (obergrenzeUeberschritten || zuLangeInaktiv) {
      stoppen();
      abmelden();
    }
  }

  function starten(): void {
    letzteAktivitaet = Date.now();
    AKTIVITAETS_EVENTS.forEach((e) => window.addEventListener(e, aktivitaet, { passive: true }));
    document.addEventListener('visibilitychange', pruefen);
    intervall = window.setInterval(pruefen, CHECK_MS);
    pruefen(); // sofort prüfen: fängt „App nach Ablauf wieder geöffnet" ab
  }

  function stoppen(): void {
    if (intervall !== undefined) {
      clearInterval(intervall);
      intervall = undefined;
    }
    AKTIVITAETS_EVENTS.forEach((e) => window.removeEventListener(e, aktivitaet));
    document.removeEventListener('visibilitychange', pruefen);
  }

  onMounted(starten);
  onUnmounted(stoppen);
}
